import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage, Language } from '../contexts/LanguageContext';
import { PlanTip } from '../components/PlanTip';

// Pedido + contrato firmado ANTES del pago (flujo: contrato -> pago Stripe -> cuestionario).
// Guarda el pedido en Supabase (Edge Function submit-order) y luego manda al cliente al enlace
// de pago de su paquete. EN/ES finalizados como borrador (revisión legal recomendada);
// HT/VI = mejor esfuerzo, pendiente de revisión nativa.
// NOTA: mientras el WhatsApp siga en 000, el mensaje de error apunta al correo (no a WhatsApp).
// 2026-10-23: al elegir impreso se avisa que llega por USPS First Class en alrededor de 5 días
// (UI.printTime). Estimado de Manuel; confirmar con la prueba real de Lob.
const ENDPOINT = 'https://jcbxwtuhshcyzlhpikxr.supabase.co/functions/v1/submit-order';
const ANON = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpjYnh3dHVoc2hjeXpsaHBpa3hyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3OTA0NDc5MzAsImV4cCI6MjEwNjAyMzkzMH0.TCCTiwKNauedwdX3R0_Ag0MCZId2nZGmuLIN86FcH-U';
const CONTRACT_VERSION = 'v1-2026-10-11';
const CONTACT_EMAIL = 'info@noorpassportservices.com';
type PlanKey = 'solo' | 'pareja' | 'familia';
const PLANS: Record<PlanKey, {
  price: number;
  persons: number;
  link: string;
}> = {
  solo: {
    price: 99,
    persons: 1,
    link: 'https://buy.stripe.com/7sY14nbDP5mLbJV081e7m01'
  },
  pareja: {
    price: 149,
    persons: 2,
    link: 'https://buy.stripe.com/fZudR99vH5mLg0b4ohe7m02'
  },
  familia: {
    price: 199,
    persons: 4,
    link: 'https://buy.stripe.com/4gM3cv23f16veW7aMFe7m03'
  }
};
const PLAN_KEYS: PlanKey[] = ['solo', 'pareja', 'familia'];
type L = Record<Language, string>;
const l = (es: string, en: string, ht: string, vi: string): L => ({
  es,
  en,
  ht,
  vi
});
const PLAN_NAME: Record<PlanKey, L> = {
  solo: l('Solo yo (1 persona)', 'Just me (1 person)', 'Sèlman mwen (1 moun)', 'Chỉ mình tôi (1 người)'),
  pareja: l('Los dos (2 personas)', 'The two of us (2 people)', 'Nou de a (2 moun)', 'Hai chúng tôi (2 người)'),
  familia: l('Mi familia (hasta 4 personas)', 'My family (up to 4 people)', 'Fanmi mwen (jiska 4 moun)', 'Gia đình tôi (tối đa 4 người)')
};

// ---------- CONTRATO ----------
type Ctx = {
  plan: string;
  price: number;
  print: boolean;
};
type Clause = {
  h: string;
  p: string;
};
type ContractDef = {
  title: string;
  head: (c: Ctx) => string;
  clauses: (c: Ctx) => Clause[];
  signed: string;
};
const NOTICE: L = l('NO SOY ABOGADO CON LICENCIA PARA EJERCER LA ABOGACÍA Y NO PUEDO DAR ASESORÍA LEGAL NI COBRAR HONORARIOS POR ASESORÍA LEGAL. NO ESTOY ACREDITADO PARA REPRESENTARLO EN ASUNTOS DE INMIGRACIÓN.', 'I AM NOT AN ATTORNEY LICENSED TO PRACTICE LAW AND MAY NOT GIVE LEGAL ADVICE OR ACCEPT FEES FOR LEGAL ADVICE. I AM NOT ACCREDITED TO REPRESENT YOU IN IMMIGRATION MATTERS.', 'MWEN PA YON AVOKA KI GEN LISANS POU PRATIKE LWA EPI MWEN PA KA BAY KONSÈY LEGAL OSWA AKSEPTE ONORÈ POU KONSÈY LEGAL. MWEN PA AKREDITE POU REPREZANTE OU NAN ZAFÈ IMIGRASYON.', 'TÔI KHÔNG PHẢI LÀ LUẬT SƯ CÓ GIẤY PHÉP HÀNH NGHỀ LUẬT VÀ KHÔNG THỂ ĐƯA RA TƯ VẤN PHÁP LÝ HOẶC NHẬN PHÍ TƯ VẤN PHÁP LÝ. TÔI KHÔNG ĐƯỢC CÔNG NHẬN ĐỂ ĐẠI DIỆN CHO QUÝ VỊ TRONG CÁC VẤN ĐỀ DI TRÚ.');
const CONTRACTS: Record<Language, ContractDef> = {
  es: {
    title: 'CONTRATO DE SERVICIO — Noor Passport Services (DBA of Noor Metabolico LLC)',
    head: c => `Paquete elegido: ${c.plan} — $${c.price} USD. Entrega: ${c.print ? 'digital por correo electrónico + paquete impreso por correo postal (opcional elegido)' : 'solo digital por correo electrónico'}.`,
    clauses: c => [{
      h: '1. El servicio',
      p: 'Noor prepara, en su idioma, los formularios de pasaporte de EE. UU. que su caso necesite, a partir de la información que usted nos entrega, para que usted los firme y los presente. Noor no es una agencia de gobierno, ni un bufete de abogados, ni notario, y no da asesoría legal ni de inmigración. El trámite ante el Departamento de Estado lo presenta usted mismo.'
    }, {
      h: '2. Aviso legal (Estatuto de Florida § 501.1391)',
      p: NOTICE.es
    }, {
      h: '3. Qué incluye',
      p: `Preparación de los formularios de su caso, revisión por un especialista y entrega digital por correo electrónico (siempre incluida). ${c.print ? 'Usted eligió además el paquete impreso, sin costo adicional, que enviaremos por correo postal a la dirección que usted indicó en este pedido.' : 'Usted eligió solo la entrega digital; no se le enviará paquete impreso.'}`
    }, {
      h: '4. Precio y pago',
      p: `El precio es $${c.price} USD por el paquete "${c.plan}". Se paga en línea (Stripe) después de firmar este contrato; no se cobra nada antes de su firma. La tarifa que cobra el gobierno de EE. UU. por el pasaporte se paga aparte, directamente al gobierno; Noor no la cobra.`
    }, {
      h: '5. Su responsabilidad',
      p: 'Usted se asegura de que los datos que entrega sean exactos y coincidan con sus documentos oficiales. Antes de la entrega final usted revisa y aprueba el formulario preparado, y lo firma y presenta siguiendo las instrucciones del gobierno.'
    }, {
      h: '6. Garantía de corrección gratis',
      p: 'Si el formulario que preparamos tiene un error nuestro, lo corregimos y lo entregamos de nuevo sin costo, en un plazo razonable. La garantía cubre solo el formulario que preparamos; no garantiza que el gobierno apruebe, acelere o no rechace su trámite, ni cubre la tarifa que usted pagó al gobierno.'
    }, {
      h: '7. Reembolsos',
      p: 'Se rigen por la Política de Reembolsos publicada en noorpassportservices.com.'
    }, {
      h: '8. Límites de responsabilidad',
      p: 'Noor no controla los tiempos ni las decisiones del Departamento de Estado ni de otra agencia. No respondemos por retrasos, rechazos o cambios de requisitos fuera de nuestro control, siempre que el formulario refleje correctamente la información que usted nos dio y aprobó.'
    }, {
      h: '9. Firma electrónica',
      p: 'Usted acepta firmar este contrato electrónicamente escribiendo su nombre completo, y que esa firma tiene el mismo efecto que una firma a mano. Guardamos una copia con fecha y hora, y se la enviamos por correo.'
    }, {
      h: '10. Privacidad',
      p: 'Usamos sus datos solo para preparar y entregar su trámite (y su dirección postal solo para enviar el paquete impreso, si lo eligió), según la Política de Privacidad del sitio.'
    }, {
      h: '11. Ley aplicable',
      p: 'Este contrato se rige por las leyes del Estado de Florida, EE. UU.'
    }],
    signed: 'Firmado electrónicamente por'
  },
  en: {
    title: 'SERVICE AGREEMENT — Noor Passport Services (DBA of Noor Metabolico LLC)',
    head: c => `Package chosen: ${c.plan} — $${c.price} USD. Delivery: ${c.print ? 'digital by email + printed package by mail (optional, chosen)' : 'digital by email only'}.`,
    clauses: c => [{
      h: '1. The service',
      p: 'Noor prepares, in your language, the U.S. passport forms your case needs, based on the information you give us, for you to sign and submit. Noor is not a government agency, a law firm, or a notary, and does not give legal or immigration advice. You submit the application to the Department of State yourself.'
    }, {
      h: '2. Legal notice (Florida Statute § 501.1391)',
      p: NOTICE.en
    }, {
      h: '3. What is included',
      p: `Preparation of the forms for your case, specialist review, and digital delivery by email (always included). ${c.print ? 'You also chose the printed package, at no extra cost, which we will mail to the address you gave in this order.' : 'You chose digital delivery only; no printed package will be sent.'}`
    }, {
      h: '4. Price and payment',
      p: `The price is $${c.price} USD for the "${c.plan}" package. It is paid online (Stripe) after you sign this agreement; nothing is charged before you sign. The fee the U.S. government charges for the passport is paid separately, directly to the government; Noor does not collect it.`
    }, {
      h: '5. Your responsibility',
      p: 'You make sure the information you provide is accurate and matches your official documents. Before final delivery you review and approve the prepared form, and you sign and submit it following the government’s instructions.'
    }, {
      h: '6. Free-correction guarantee',
      p: 'If the form we prepared has an error on our part, we will correct it and redeliver it at no cost, within a reasonable time. The guarantee covers only the form we prepared; it does not guarantee that the government will approve, expedite, or not reject your application, nor does it cover the fee you paid to the government.'
    }, {
      h: '7. Refunds',
      p: 'Refunds follow the Refund Policy published at noorpassportservices.com.'
    }, {
      h: '8. Limits of liability',
      p: 'Noor does not control the timing or decisions of the Department of State or any other agency. We are not responsible for delays, rejections, or changes in requirements outside our control, as long as the form correctly reflects the information you gave us and approved.'
    }, {
      h: '9. Electronic signature',
      p: 'You agree to sign this agreement electronically by typing your full name, and that this signature has the same effect as a handwritten one. We keep a dated copy and email it to you.'
    }, {
      h: '10. Privacy',
      p: 'We use your information only to prepare and deliver your paperwork (and your mailing address only to send the printed package, if you chose it), under the site’s Privacy Policy.'
    }, {
      h: '11. Governing law',
      p: 'This agreement is governed by the laws of the State of Florida, USA.'
    }],
    signed: 'Electronically signed by'
  },
  ht: {
    title: 'KONTRA SÈVIS — Noor Passport Services (DBA of Noor Metabolico LLC)',
    head: c => `Pakè ou chwazi: ${c.plan} — $${c.price} USD. Livrezon: ${c.print ? 'dijital pa imèl + pakè enprime pa lapòs (opsyònèl, ou chwazi li)' : 'dijital pa imèl sèlman'}.`,
    clauses: c => [{
      h: '1. Sèvis la',
      p: 'Noor prepare, nan lang ou, fòm paspò ameriken ka ou a bezwen yo, dapre enfòmasyon ou ban nou, pou ou siyen epi soumèt yo. Noor se pa yon ajans gouvènman, se pa yon kabinè avoka, ni yon notè, epi nou pa bay konsèy legal ni konsèy imigrasyon. Se ou menm ki soumèt aplikasyon an bay Depatman Deta a.'
    }, {
      h: '2. Avi legal (Lwa Florid § 501.1391)',
      p: NOTICE.ht
    }, {
      h: '3. Sa ki enkli',
      p: `Preparasyon fòm ka ou a, revizyon pa yon espesyalis, ak livrezon dijital pa imèl (toujou enkli). ${c.print ? 'Ou te chwazi tou pakè enprime a, san kou adisyonèl, nou pral voye l pa lapòs nan adrès ou te bay nan kòmand sa a.' : 'Ou te chwazi livrezon dijital sèlman; nou p ap voye pakè enprime.'}`
    }, {
      h: '4. Pri ak peman',
      p: `Pri a se $${c.price} USD pou pakè "${c.plan}" la. Ou peye sou entènèt (Stripe) apre ou siyen kontra sa a; yo pa chaje anyen anvan ou siyen. Frè gouvènman ameriken an chaje pou paspò la peye apa, dirèkteman bay gouvènman an; Noor pa kolekte li.`
    }, {
      h: '5. Responsablite ou',
      p: 'Ou asire enfòmasyon ou bay yo egzat epi yo koresponn ak dokiman ofisyèl ou yo. Anvan livrezon final la, ou revize epi apwouve fòm ki prepare a, epi ou siyen li epi soumèt li dapre enstriksyon gouvènman an.'
    }, {
      h: '6. Garanti korije gratis',
      p: 'Si fòm nou prepare a gen yon erè ki soti nan bò nou, nou korije l epi nou remèt li san kou, nan yon dele rezonab. Garanti a kouvri sèlman fòm nou prepare a; li pa garanti gouvènman an ap apwouve, akselere, oswa pa rejte aplikasyon ou, ni li pa kouvri frè ou te peye gouvènman an.'
    }, {
      h: '7. Ranbousman',
      p: 'Yo swiv Politik Ranbousman ki pibliye sou noorpassportservices.com.'
    }, {
      h: '8. Limit responsablite',
      p: 'Noor pa kontwole tan ni desizyon Depatman Deta a oswa okenn lòt ajans. Nou pa responsab pou reta, rejè, oswa chanjman kondisyon ki ande yò kontwòl nou, depi fòm nan reflete korèkteman enfòmasyon ou ban nou epi apwouve.'
    }, {
      h: '9. Siyati elektwonik',
      p: 'Ou aksepte siyen kontra sa a elektwonikman lè ou tape non konplè ou, epi siyati sa a gen menm efè ak yon siyati alamen. Nou kenbe yon kopi ak dat, epi nou voye l ba ou pa imèl.'
    }, {
      h: '10. Vi prive',
      p: 'Nou itilize enfòmasyon ou sèlman pou prepare ak livre dosye ou (epi adrès postal ou sèlman pou voye pakè enprime a, si ou chwazi l), dapre Politik Vi Prive sit la.'
    }, {
      h: '11. Lwa ki aplikab',
      p: 'Kontra sa a gouvène pa lwa Eta Florid, Etazini.'
    }],
    signed: 'Siyen elektwonikman pa'
  },
  vi: {
    title: 'HỢP ĐỒNG DỊCH VỤ — Noor Passport Services (DBA of Noor Metabolico LLC)',
    head: c => `Gói đã chọn: ${c.plan} — $${c.price} USD. Giao hồ sơ: ${c.print ? 'bản kỹ thuật số qua email + bộ hồ sơ in gửi qua bưu điện (tùy chọn, đã chọn)' : 'chỉ bản kỹ thuật số qua email'}.`,
    clauses: c => [{
      h: '1. Dịch vụ',
      p: 'Noor chuẩn bị, bằng ngôn ngữ của quý vị, các mẫu đơn hộ chiếu Hoa Kỳ mà trường hợp của quý vị cần, dựa trên thông tin quý vị cung cấp, để quý vị ký và nộp. Noor không phải là cơ quan chính phủ, công ty luật hay công chứng viên, và không tư vấn pháp lý hay di trú. Chính quý vị nộp đơn cho Bộ Ngoại giao.'
    }, {
      h: '2. Thông báo pháp lý (Đạo luật Florida § 501.1391)',
      p: NOTICE.vi
    }, {
      h: '3. Bao gồm gì',
      p: `Chuẩn bị các mẫu đơn cho trường hợp của quý vị, chuyên viên xem lại, và giao bản kỹ thuật số qua email (luôn bao gồm). ${c.print ? 'Quý vị cũng đã chọn bộ hồ sơ in, không tính thêm phí, sẽ được gửi qua bưu điện đến địa chỉ quý vị đã cung cấp trong đơn này.' : 'Quý vị chỉ chọn giao bản kỹ thuật số; sẽ không gửi bộ hồ sơ in.'}`
    }, {
      h: '4. Giá và thanh toán',
      p: `Giá là $${c.price} USD cho gói "${c.plan}". Thanh toán trực tuyến (Stripe) sau khi quý vị ký hợp đồng này; không thu tiền trước khi ký. Lệ phí chính phủ Hoa Kỳ thu cho hộ chiếu được trả riêng, trực tiếp cho chính phủ; Noor không thu khoản này.`
    }, {
      h: '5. Trách nhiệm của quý vị',
      p: 'Quý vị đảm bảo thông tin cung cấp chính xác và khớp với giấy tờ chính thức. Trước khi giao cuối cùng, quý vị xem và phê duyệt mẫu đơn đã chuẩn bị, rồi ký và nộp theo hướng dẫn của chính phủ.'
    }, {
      h: '6. Bảo đảm sửa lỗi miễn phí',
      p: 'Nếu mẫu đơn chúng tôi chuẩn bị có lỗi do chúng tôi, chúng tôi sẽ sửa và giao lại miễn phí trong thời gian hợp lý. Bảo đảm chỉ áp dụng cho mẫu đơn chúng tôi chuẩn bị; không đảm bảo chính phủ sẽ phê duyệt, đẩy nhanh hay không từ chối, và không bao gồm lệ phí quý vị đã trả cho chính phủ.'
    }, {
      h: '7. Hoàn tiền',
      p: 'Theo Chính sách Hoàn tiền đăng trên noorpassportservices.com.'
    }, {
      h: '8. Giới hạn trách nhiệm',
      p: 'Noor không kiểm soát thời gian hay quyết định của Bộ Ngoại giao hay cơ quan khác. Chúng tôi không chịu trách nhiệm về chậm trễ, từ chối hay thay đổi yêu cầu ngoài tầm kiểm soát, miễn là mẫu đơn phản ánh chính xác thông tin quý vị đã cung cấp và phê duyệt.'
    }, {
      h: '9. Chữ ký điện tử',
      p: 'Quý vị đồng ý ký hợp đồng này bằng cách gõ họ tên đầy đủ, và chữ ký này có giá trị như chữ ký tay. Chúng tôi lưu bản sao có ngày giờ và gửi email cho quý vị.'
    }, {
      h: '10. Quyền riêng tư',
      p: 'Chúng tôi chỉ dùng thông tin để chuẩn bị và giao hồ sơ (địa chỉ bưu điện chỉ để gửi bộ hồ sơ in nếu quý vị chọn), theo Chính sách Quyền riêng tư của trang web.'
    }, {
      h: '11. Luật áp dụng',
      p: 'Hợp đồng này chịu sự điều chỉnh của luật Tiểu bang Florida, Hoa Kỳ.'
    }],
    signed: 'Ký điện tử bởi'
  }
};
function buildContract(lang: Language, c: Ctx): string {
  const d = CONTRACTS[lang];
  return [d.title, d.head(c), ...d.clauses(c).map(x => `${x.h}\n${x.p}`)].join('\n\n');
}

// ---------- TEXTOS DE LA PÁGINA ----------
const UI = {
  title: l('Su pedido y contrato', 'Your order and agreement', 'Kòmand ou ak kontra a', 'Đơn đặt hàng và hợp đồng'),
  intro: l('Elija su paquete, díganos cómo quiere recibir su formulario, lea y firme el contrato. Después pasa al pago. No se cobra nada antes de firmar.', 'Choose your package, tell us how you want to receive your form, then read and sign the agreement. Payment comes next. Nothing is charged before you sign.', 'Chwazi pakè ou, di nou kijan ou vle resevwa fòm ou, li epi siyen kontra a. Apre sa ou pase nan peman. Yo pa chaje anyen anvan ou siyen.', 'Chọn gói, cho chúng tôi biết cách nhận mẫu đơn, đọc và ký hợp đồng. Sau đó qua bước thanh toán. Không thu tiền trước khi ký.'),
  s1: l('1. Su paquete', '1. Your package', '1. Pakè ou', '1. Gói của quý vị'),
  s2: l('2. Sus datos', '2. Your details', '2. Enfòmasyon ou', '2. Thông tin của quý vị'),
  name: l('Nombre completo', 'Full name', 'Non konplè', 'Họ và tên'),
  email: l('Correo electrónico', 'Email', 'Imèl', 'Email'),
  phone: l('Teléfono (opcional)', 'Phone (optional)', 'Telefòn (opsyònèl)', 'Điện thoại (không bắt buộc)'),
  s3: l('3. Cómo recibe su formulario', '3. How you receive your form', '3. Kijan ou resevwa fòm ou', '3. Cách nhận mẫu đơn'),
  digital: l('Solo digital (por correo electrónico)', 'Digital only (by email)', 'Dijital sèlman (pa imèl)', 'Chỉ bản kỹ thuật số (qua email)'),
  digitalSub: l('Siempre incluido.', 'Always included.', 'Toujou enkli.', 'Luôn được bao gồm.'),
  print: l('Digital + paquete impreso por correo postal', 'Digital + printed package by mail', 'Dijital + pakè enprime pa lapòs', 'Kỹ thuật số + bộ hồ sơ in gửi bưu điện'),
  printSub: l('Opcional, sin costo adicional.', 'Optional, no extra cost.', 'Opsyònèl, san kou adisyonèl.', 'Tùy chọn, không tính thêm phí.'),
  printTime: l('Tenga en cuenta: el paquete impreso se envía por USPS First Class y demora alrededor de 5 días en llegar a su casa. Su formulario digital le llega por correo electrónico mucho antes.', 'Please note: the printed package is mailed by USPS First Class and takes about 5 days to reach your home. Your digital form arrives by email much sooner.', 'Tanpri note: pakè enprime a voye pa USPS First Class epi li pran apeprè 5 jou pou li rive lakay ou. Fòm dijital ou a rive pa imèl anpil pi bonè.', 'Lưu ý: bộ hồ sơ in được gửi qua USPS First Class và mất khoảng 5 ngày để đến nhà quý vị. Bản kỹ thuật số sẽ đến qua email sớm hơn nhiều.'),
  printIncl: l('(El paquete impreso incluye: su formulario a color, listo para firmar; instrucciones para sacar la cita y un ejemplo de cómo llenar el cheque para pagar la tarifa al Departamento de Estado; preguntas frecuentes; e información sobre nuestros servicios opcionales.)', '(The printed package includes: your printed color form, ready to sign; instructions for getting your appointment and an example of how to fill out the check to pay the fee to the U.S. Department of State; frequently asked questions; and information about our optional services.)', '(Pakè enprime a gen ladan l: fòm ou enprime an koulè, pare pou siyen; enstriksyon pou pran randevou a ak yon egzanp kijan pou ranpli chèk la pou peye frè a bay Depatman Deta Ameriken an; kesyon moun poze souvan; ak enfòmasyon sou sèvis opsyònèl nou yo.)', '(Bộ hồ sơ in gồm: mẫu đơn của quý vị in màu, sẵn sàng để ký; hướng dẫn đặt lịch hẹn và ví dụ cách điền séc để trả lệ phí cho Bộ Ngoại giao Hoa Kỳ; các câu hỏi thường gặp; và thông tin về các dịch vụ tùy chọn của chúng tôi.)'),
  addrTitle: l('Dirección para enviar el paquete impreso', 'Address to mail the printed package', 'Adrès pou voye pakè enprime a', 'Địa chỉ gửi bộ hồ sơ in'),
  line1: l('Dirección (calle y número)', 'Street address', 'Adrès (ri ak nimèwo)', 'Địa chỉ (số nhà, đường)'),
  line2: l('Apto / unidad (opcional)', 'Apt / unit (optional)', 'Apa / inite (opsyònèl)', 'Căn hộ / phòng (không bắt buộc)'),
  city: l('Ciudad', 'City', 'Vil', 'Thành phố'),
  state: l('Estado (2 letras, ej. FL)', 'State (2 letters, e.g. FL)', 'Eta (2 lèt, ex. FL)', 'Tiểu bang (2 chữ, ví dụ FL)'),
  zip: l('Código postal (5 dígitos)', 'ZIP code (5 digits)', 'Kòd postal (5 chif)', 'Mã ZIP (5 số)'),
  s4: l('4. Contrato de servicio', '4. Service agreement', '4. Kontra sèvis', '4. Hợp đồng dịch vụ'),
  accept: l('He leído el contrato y estoy de acuerdo.', 'I have read the agreement and I agree.', 'Mwen li kontra a epi mwen dakò.', 'Tôi đã đọc hợp đồng và đồng ý.'),
  sign: l('Firma: escriba su nombre completo', 'Signature: type your full name', 'Siyati: tape non konplè ou', 'Chữ ký: gõ họ tên đầy đủ'),
  go: l('Firmar y continuar al pago', 'Sign and continue to payment', 'Siyen epi kontinye nan peman', 'Ký và tiếp tục thanh toán'),
  sending: l('Guardando…', 'Saving…', 'N ap anrejistre…', 'Đang lưu…'),
  err: l('No pudimos guardar su pedido. Revise sus datos e intente de nuevo, o escríbanos a info@noorpassportservices.com.', 'We could not save your order. Check your details and try again, or email us at info@noorpassportservices.com.', 'Nou pa t kapab anrejistre kòmand ou. Verifye enfòmasyon ou epi eseye ankò, oswa ekri nou nan info@noorpassportservices.com.', 'Chúng tôi không thể lưu đơn. Hãy kiểm tra và thử lại, hoặc gửi email cho chúng tôi tại info@noorpassportservices.com.'),
  missing: l('Complete todos los campos y marque la casilla del contrato.', 'Fill in all fields and tick the agreement box.', 'Ranpli tout chan yo epi make bwèt kontra a.', 'Hãy điền đủ các mục và đánh dấu ô hợp đồng.'),
  sigMismatch: l('La firma debe coincidir con su nombre completo.', 'The signature must match your full name.', 'Siyati a dwe koresponn ak non konplè ou.', 'Chữ ký phải khớp với họ tên đầy đủ.'),
  note: l('Una copia del contrato le llegará por correo. Si ya pagó antes, escríbanos.', 'A copy of the agreement will be emailed to you.', 'N ap voye yon kopi kontra a ba ou pa imèl.', 'Một bản sao hợp đồng sẽ được gửi qua email.'),
  extra: l('¿Más de 4 personas? Escríbanos a', 'More than 4 people? Email us at', 'Plis pase 4 moun? Ekri nou nan', 'Hơn 4 người? Hãy gửi email cho chúng tôi tại')
};
const inputCls = 'w-full rounded-xl border border-brand-border bg-white px-4 py-3 text-brand-textPrimary focus:outline-none focus:ring-2 focus:ring-brand-accent';
const labelCls = 'block text-sm font-medium text-brand-textPrimary mb-1';
export function PedidoPage() {
  const {
    language
  } = useLanguage();
  const [plan, setPlan] = useState<PlanKey>(() => {
    try {
      const p = new URLSearchParams(window.location.search).get('plan') as PlanKey;
      return PLAN_KEYS.includes(p) ? p : 'solo';
    } catch {
      return 'solo';
    }
  });
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [print, setPrint] = useState(false);
  const [a, setA] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    zip: ''
  });
  const [ok, setOk] = useState(false);
  const [sig, setSig] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const planLabel = PLAN_NAME[plan][language];
  const contract = useMemo(() => buildContract(language, {
    plan: planLabel,
    price: PLANS[plan].price,
    print
  }), [language, planLabel, plan, print]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg('');
    const addrOk = !print || a.line1.trim() && a.city.trim() && a.state.trim() && /^\d{5}(-\d{4})?$/.test(a.zip.trim());
    if (!name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) || !addrOk || !ok || !sig.trim()) {
      setMsg(UI.missing[language]);
      return;
    }
    const norm = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();
    if (norm(sig) !== norm(name)) {
      setMsg(UI.sigMismatch[language]);
      return;
    }
    setBusy(true);
    try {
      const signedAt = new Date().toISOString();
      const text = `${contract}\n\n${CONTRACTS[language].signed}: ${sig.trim()} — ${signedAt}`;
      const r = await fetch(ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: ANON,
          Authorization: `Bearer ${ANON}`
        },
        body: JSON.stringify({
          plan,
          persons: PLANS[plan].persons,
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          lang: language,
          delivery: print ? 'digital_print' : 'digital',
          address: print ? {
            ...a,
            state: a.state.trim().toUpperCase()
          } : null,
          contract_version: CONTRACT_VERSION,
          contract_text: text,
          signature: sig.trim(),
          accepted: true
        })
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error('save');
      const url = `${PLANS[plan].link}?client_reference_id=${encodeURIComponent(j.id)}&prefilled_email=${encodeURIComponent(email.trim())}`;
      window.location.href = url;
    } catch {
      setMsg(UI.err[language]);
      setBusy(false);
    }
  };
  return <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-id="element-636" data-mp="3d25taeaip2rd">
      <h1 className="text-3xl sm:text-4xl font-bold text-brand-textPrimary mb-3" data-id="element-637" data-mp="3qsrcy57spgqb">{UI.title[language]}</h1>
      <p className="text-lg text-brand-textSecondary mb-8" data-id="element-638" data-mp="3m22kamkdqdpl">{UI.intro[language]}</p>

      <form onSubmit={submit} className="space-y-8" data-id="element-639" data-mp="3m07yz7574jkz">
        <section data-id="element-640" data-mp="2ybf3fbshkdqw">
          <h2 className="text-xl font-semibold text-brand-textPrimary mb-3" data-id="element-641" data-mp="2yax4g7efvlfa">{UI.s1[language]}</h2>
          <div className="grid gap-3" data-id="element-642" data-mp="2uad8h75lqnuq">
            {PLAN_KEYS.map(k => <label key={k} className={`relative group flex items-center justify-between gap-3 rounded-xl border px-4 py-3 cursor-pointer ${plan === k ? 'border-brand-accent bg-brand-accent/5' : 'border-brand-border bg-white'}`} data-id="element-643" data-mp="2k5wuykflhiu3">
                <span className="flex items-center gap-3" data-id="element-644" data-mp="2a6ntz7mbb3y8">
                  <input type="radio" name="plan" checked={plan === k} onChange={() => setPlan(k)} className="w-4 h-4" data-id="element-645" data-mp="2f8wq2cuhem4k" />
                  <span className="font-medium text-brand-textPrimary" data-id="element-646" data-mp="32xz6jhddq8xh">{PLAN_NAME[k][language]}</span>
                </span>
                <span className="font-bold text-brand-textPrimary" data-id="element-647" data-mp="37ug22wmwrpkl">${PLANS[k].price}</span>
                <PlanTip language={language} data-id="element-648" data-mp="2t1closl1zhvg" />
              </label>)}
          </div>
          <p className="text-sm text-brand-textSecondary mt-2" data-id="element-649" data-mp="2xwi8ruq6z2l4">
            {UI.extra[language]}{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="underline font-medium text-brand-textPrimary" data-id="element-650" data-mp="1hm7gnhxg978o">{CONTACT_EMAIL}</a>
          </p>
        </section>

        <section data-id="element-651" data-mp="3p64nl6k15imm">
          <h2 className="text-xl font-semibold text-brand-textPrimary mb-3" data-id="element-652" data-mp="3p4jsnftszxo8">{UI.s2[language]}</h2>
          <div className="grid gap-4 sm:grid-cols-2" data-id="element-653" data-mp="3k8310bneb16e">
            <div className="sm:col-span-2" data-id="element-654" data-mp="3ffs29c4e5lff">
              <label className={labelCls} htmlFor="p-name" data-id="element-655" data-mp="3feqo2ys2with">{UI.name[language]}</label>
              <input id="p-name" className={inputCls} value={name} onChange={e => setName(e.target.value)} autoComplete="name" data-id="element-656" data-mp="3afxe3gc039y2" />
            </div>
            <div data-id="element-657" data-mp="35fj3prmxfnwv">
              <label className={labelCls} htmlFor="p-email" data-id="element-658" data-mp="31giki80jej9c">{UI.email[language]}</label>
              <input id="p-email" type="email" className={inputCls} value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" data-id="element-659" data-mp="2wh62c0km20d3" />
            </div>
            <div data-id="element-660" data-mp="2rjxgnq097e73">
              <label className={labelCls} htmlFor="p-phone" data-id="element-661" data-mp="2rkflvc4w92r5">{UI.phone[language]}</label>
              <input id="p-phone" type="tel" className={inputCls} value={phone} onChange={e => setPhone(e.target.value)} autoComplete="tel" data-id="element-662" data-mp="up1aep34332d" />
            </div>
          </div>
        </section>

        <section data-id="element-663" data-mp="x9z25lf2zmyn">
          <h2 className="text-xl font-semibold text-brand-textPrimary mb-3" data-id="element-664" data-mp="x8egklaqvecx">{UI.s3[language]}</h2>
          <div className="grid gap-3" data-id="element-665" data-mp="126z6nrwa8hnn">
            <label className={`flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer ${!print ? 'border-brand-accent bg-brand-accent/5' : 'border-brand-border bg-white'}`} data-id="element-666" data-mp="16zsdclbba02d">
              <input type="radio" name="delivery" checked={!print} onChange={() => setPrint(false)} className="w-4 h-4 mt-1" data-id="element-667" data-mp="ebwi7l1e4g7p" />
              <span data-id="element-668" data-mp="jbixs14oy8jo">
                <span className="block font-medium text-brand-textPrimary" data-id="element-669" data-mp="japxnj08mhai">{UI.digital[language]}</span>
                <span className="block text-sm text-brand-textSecondary" data-id="element-670" data-mp="o89acehaem0c">{UI.digitalSub[language]}</span>
              </span>
            </label>
            <label className={`flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer ${print ? 'border-brand-accent bg-brand-accent/5' : 'border-brand-border bg-white'}`} data-id="element-671" data-mp="1zp8zx2c92mqo">
              <input type="radio" name="delivery" checked={print} onChange={() => setPrint(true)} className="w-4 h-4 mt-1" data-id="element-672" data-mp="2pruwbwl6zvzv" />
              <span data-id="element-673" data-mp="2ktjkxhf91dfj">
                <span className="block font-medium text-brand-textPrimary" data-id="element-674" data-mp="2ku2zqp9lrlpc">{UI.print[language]}</span>
                <span className="block text-sm text-brand-textSecondary" data-id="element-675" data-mp="2zj8zceccrdxr">{UI.printSub[language]}</span>
                <span className="block text-sm text-brand-textSecondary mt-1" data-id="element-676" data-mp="2upd6fqay4h9g">{UI.printIncl[language]}</span>
              </span>
            </label>
          </div>
          {print && <div className="mt-4 rounded-xl border border-brand-accent/40 bg-brand-accent/5 p-4" data-id="element-677" data-mp="34fzdgw4ljzgj">
              <p className="text-sm font-medium text-brand-textPrimary" role="note" data-id="element-678" data-mp="3if7hc2bpfvr2">{UI.printTime[language]}</p>
            </div>}
          {print && <div className="mt-4 rounded-xl border border-brand-border bg-white p-4" data-id="element-679" data-mp="1o296nhm3ex37">
              <p className="font-medium text-brand-textPrimary mb-3" data-id="element-680" data-mp="1j3ehlxxj8can">{UI.addrTitle[language]}</p>
              <div className="grid gap-3 sm:grid-cols-6" data-id="element-681" data-mp="3uqrpgv37ta73">
                <div className="sm:col-span-4" data-id="element-682" data-mp="3ur9w8lni76t9">
                  <label className={labelCls} htmlFor="a1" data-id="element-683" data-mp="3k751u7o10j1">{UI.line1[language]}</label>
                  <input id="a1" className={inputCls} value={a.line1} onChange={e => setA({
                ...a,
                line1: e.target.value
              })} autoComplete="address-line1" data-id="element-684" data-mp="3l0yf1xet714f" />
                </div>
                <div className="sm:col-span-2" data-id="element-685" data-mp="3pxyl48bns3vj">
                  <label className={labelCls} htmlFor="a2" data-id="element-686" data-mp="3c00ks6u6a7z1">{UI.line2[language]}</label>
                  <input id="a2" className={inputCls} value={a.line2} onChange={e => setA({
                ...a,
                line2: e.target.value
              })} autoComplete="address-line2" data-id="element-687" data-mp="3gz3bsndbido0" />
                </div>
                <div className="sm:col-span-3" data-id="element-688" data-mp="324gr7r32zldg">
                  <label className={labelCls} htmlFor="a3" data-id="element-689" data-mp="3728gedccctdy">{UI.city[language]}</label>
                  <input id="a3" className={inputCls} value={a.city} onChange={e => setA({
                ...a,
                city: e.target.value
              })} autoComplete="address-level2" data-id="element-690" data-mp="10ahi9cdflg3x" />
                </div>
                <div className="sm:col-span-1" data-id="element-691" data-mp="26ipv5fxipd4b">
                  <label className={labelCls} htmlFor="a4" data-id="element-692" data-mp="26k0yjen1wiuw">{UI.state[language]}</label>
                  <input id="a4" maxLength={2} className={inputCls} value={a.state} onChange={e => setA({
                ...a,
                state: e.target.value
              })} autoComplete="address-level1" data-id="element-693" data-mp="2akun8a8bi1ou" />
                </div>
                <div className="sm:col-span-2" data-id="element-694" data-mp="2kawaizsujg2p">
                  <label className={labelCls} htmlFor="a5" data-id="element-695" data-mp="2kbpbtl91dddg">{UI.zip[language]}</label>
                  <input id="a5" inputMode="numeric" className={inputCls} value={a.zip} onChange={e => setA({
                ...a,
                zip: e.target.value
              })} autoComplete="postal-code" data-id="element-696" data-mp="2pb1vxy8iw6cc" />
                </div>
              </div>
            </div>}
        </section>

        <section data-id="element-697" data-mp="338yrqsf5k8py">
          <h2 className="text-xl font-semibold text-brand-textPrimary mb-3" data-id="element-698" data-mp="337faozlo9tn4">{UI.s4[language]}</h2>
          <div className="max-h-80 overflow-y-auto rounded-xl border border-brand-border bg-white p-4 text-sm text-brand-textSecondary whitespace-pre-wrap leading-relaxed" tabIndex={0} data-id="element-699" data-mp="13wwc5t9nfyhk">
            {contract}
          </div>
          <label className="flex items-start gap-3 mt-4 cursor-pointer" data-id="element-700" data-mp="11d9h6cspjm2w">
            <input type="checkbox" checked={ok} onChange={e => setOk(e.target.checked)} className="w-5 h-5 mt-0.5" data-id="element-701" data-mp="wjojymyxuw4r" />
            <span className="text-brand-textPrimary" data-id="element-702" data-mp="rln1o49c70uc">{UI.accept[language]}</span>
          </label>
          <div className="mt-4" data-id="element-703" data-mp="mo3kavhce7y6">
            <label className={labelCls} htmlFor="p-sig" data-id="element-704" data-mp="1k9skumkqshs2">{UI.sign[language]}</label>
            <input id="p-sig" className={`${inputCls} italic`} value={sig} onChange={e => setSig(e.target.value)} autoComplete="off" data-id="element-705" data-mp="1k7ohq6uo3msu" />
          </div>
        </section>

        {msg && <p role="alert" className="text-red-700 font-medium" data-id="element-706" data-mp="1aa1qckw6m2rz">{msg}</p>}
        <button type="submit" disabled={busy} className="w-full sm:w-auto inline-flex items-center justify-center bg-brand-accent hover:bg-brand-accentHover text-brand-textPrimary px-8 py-4 rounded-full font-semibold text-lg disabled:opacity-60" data-id="element-707" data-mp="169837tqkcs05">
          {busy ? UI.sending[language] : `${UI.go[language]} — $${PLANS[plan].price}`}
        </button>
        <p className="text-sm text-brand-textSecondary" data-id="element-708" data-mp="3sft923pjed8g">{UI.note[language]}</p>
        <p className="text-sm text-brand-textSecondary" data-id="element-709" data-mp="1al54x8g0b5h">
          <Link to="/terms-of-service" className="underline" data-id="element-710" data-mp="3ik9lq5p1gmvb">Terms</Link> · <Link to="/privacy-policy" className="underline" data-id="element-711" data-mp="3nikqw34e2977">Privacy</Link> · <Link to="/refund-policy" className="underline" data-id="element-712" data-mp="3ngq5yot4ah34">Refunds</Link>
        </p>
      </form>
    </div>;
}